import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/KpiCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFilters } from "@/context/FilterContext";
import { api, errMsg } from "@/lib/api";

const Cellule = ({ v }) => <td className="py-1.5 px-2 text-right tabular">{v || <span className="text-slate-300">0</span>}</td>;

const Rattacher = ({ annonce, fiches, i, onDone }) => {
  const { refreshOptions } = useFilters();
  const [envoi, setEnvoi] = useState(false);
  const choisir = async (logement) => {
    setEnvoi(true);
    try {
      const { data } = await api.post("/logements/rattacher", { annonce, logement });
      toast.success(`« ${annonce} » rattaché à ${data.logement} : ${data.deplacees} réservation(s) déplacée(s)`);
      refreshOptions();
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  if (envoi) return <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-slate-400" />;
  return (
    <Select onValueChange={choisir}>
      <SelectTrigger className="ml-2 inline-flex h-7 w-auto gap-1 border-red-200 bg-white px-2 text-xs text-red-700" data-testid={`par-logement-rattacher-${i}`}>
        <SelectValue placeholder="Rattacher à…" />
      </SelectTrigger>
      <SelectContent>
        {fiches.map((f) => <SelectItem key={f.nom} value={f.nom} data-testid={`par-logement-rattacher-${i}-option-${f.nom}`}>{f.nom}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const ParLogementPanel = ({ rows, onChange }) => {
  const inconnus = (rows || []).filter((r) => !r.connu).length;
  const [fiches, setFiches] = useState([]);
  useEffect(() => {
    if (inconnus) { api.get("/logements/config").then((r) => setFiches(r.data)).catch(() => setFiches([])); }
  }, [inconnus]);
  if (!rows?.length) return null;
  return (
    <Panel title="Réservations par logement" testId="par-logement-panel"
      action={inconnus ? <span className="text-xs font-medium text-red-600" data-testid="par-logement-corriger">{inconnus} nom(s) inconnu(s) à rattacher</span> : null}>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          <th className="pb-1 text-left">Logement</th><th className="pb-1 px-2 text-right">Airbnb</th><th className="pb-1 px-2 text-right">Booking</th>
          <th className="pb-1 px-2 text-right">Autres</th><th className="pb-1 px-2 text-right">Total</th><th className="pb-1 pl-2 text-right">Nuits</th>
        </tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.logement} className={`border-t border-slate-100 ${r.connu ? "" : "bg-red-50/60"}`} data-testid={`par-logement-row-${i}`}>
            <td className="py-1.5 pr-2">
              <span className={r.connu ? "text-slate-800" : "font-medium text-red-700"}>{r.logement}</span>
              {!r.connu && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-red-600" data-testid={`par-logement-inconnu-${i}`}><AlertTriangle className="h-3 w-3" />nom inconnu</span>}
              {!r.connu && fiches.length > 0 && <Rattacher annonce={r.logement} fiches={fiches} i={i} onDone={onChange} />}
            </td>
            <Cellule v={r.Airbnb} /><Cellule v={r.Booking} /><Cellule v={r.Autres} />
            <td className="py-1.5 px-2 text-right tabular font-semibold" data-testid={`par-logement-total-${i}`}>{r.total}</td>
            <td className="py-1.5 pl-2 text-right tabular text-slate-500">{r.nuits}</td>
          </tr>
        ))}</tbody>
      </table>
      {inconnus > 0 && <p className="mt-2 text-[11px] text-slate-500">Un nom inconnu ne correspond à aucune fiche logement. Choisissez « Rattacher à… » : le nom est mémorisé pour les prochains imports et toutes ses réservations sont déplacées. Vous pouvez aussi le gérer dans <Link to="/parametres" className="underline">Paramètres › Logements</Link>.</p>}
    </Panel>
  );
};
